import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAuth } from '../../context/AuthContext';
import ConsentModal from '../ConsentModal';
import { createConsentData, setConsentByEmail } from '../../services/consentService';
import { 
  DashboardSkeleton, 
  GenericPageSkeleton,
  HighlightsSkeleton,
  FormSkeleton,
  UserProfileSkeleton,
  PostCruiseDetailsSkeleton,
  CruiseDetailsSkeleton,
  FriendsSkeleton,
  FriendCruisesSkeleton
} from '../LoadingSkeleton';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { currentUser, loading, consentRequired, updateUserConsent, clearConsentRequired, logout } = useAuth();
  const location = useLocation();
  const isNative = Capacitor.getPlatform() !== 'web';
  const [showRedirectSkeleton, setShowRedirectSkeleton] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  // Hide splash screen when ProtectedRoute first mounts on native platforms.
  // SplashScreen.hide() is idempotent — safe to call multiple times.
  // Fires unconditionally so the splash is always dismissed even if loading
  // resolves slowly or the skeleton renders before auth completes.
  useEffect(() => {
    if (isNative) {
      SplashScreen.hide();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle redirect with skeleton on native platforms
  useEffect(() => {
    if (!loading && !currentUser && isNative) {
      setShowRedirectSkeleton(true);
      // Navigate after showing skeleton briefly
      const timer = setTimeout(() => {
        setShowRedirectSkeleton(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, currentUser, isNative]);

  // Show consent modal when consent is required
  useEffect(() => {
    if (!loading && currentUser && consentRequired && currentUser.emailVerified) {
      setShowConsentModal(true);
    } else {
      setShowConsentModal(false);
    }
  }, [loading, currentUser, consentRequired]);

  const handleConsentResponse = async (ageVerified: boolean, termsAccepted: boolean) => {
    setShowConsentModal(false);
    
    if (ageVerified && termsAccepted && currentUser?.email) {
      try {
        // Create consent data and update user record
        const consentData = createConsentData(true, currentUser.email, ageVerified, termsAccepted);
        await updateUserConsent(consentData);
        
        // Store locally as well
        setConsentByEmail(currentUser.email, consentData);
        
        // Clear the consent required flag
        clearConsentRequired();
      } catch (error) {
        console.error('Failed to update consent:', error);
        // If consent update fails, log user out for security
        try {
          await logout();
        } catch (logoutError) {
          console.error('Failed to logout:', logoutError);
        }
      }
    } else {
      // User didn't complete consent requirements - log them out
      try {
        await logout();
      } catch (error) {
        console.error('Failed to logout:', error);
      }
    }
  };

  // Get appropriate skeleton based on current route
  const getSkeletonForRoute = () => {
    const path = location.pathname;
    
    if (path === '/dashboard') {
      return <DashboardSkeleton />;
    } else if (path === '/highlights') {
      return <HighlightsSkeleton />;
    } else if (path === '/profile') {
      return <UserProfileSkeleton />;
    } else if (path === '/friends') {
      return <FriendsSkeleton />;
    } else if (path.startsWith('/friend/') && path.endsWith('/cruises')) {
      return <FriendCruisesSkeleton />;
    } else if (path.startsWith('/cruise/')) {
      return <CruiseDetailsSkeleton />;
    } else if (path.startsWith('/post-cruise-details/')) {
      return <PostCruiseDetailsSkeleton />;
    } else if (path === '/add-cruise' || path.startsWith('/edit-cruise/')) {
      return <FormSkeleton />;
    } else {
      return <GenericPageSkeleton />;
    }
  };

  // Platform agnostic: show skeleton while Firebase restores session
  if (loading) {
    const skeleton = getSkeletonForRoute();
    console.log(`[ProtectedRoute] Showing skeleton for route: ${location.pathname}`);
    return skeleton;
  }

  if (!currentUser) {
    // On native platforms: show skeleton briefly while redirecting to avoid blank screen
    if (isNative && showRedirectSkeleton) {
      return getSkeletonForRoute();
    }
    return <Navigate to="/login" />;
  }

  if (!currentUser.emailVerified) {
    return <Navigate to="/verify-email" />;
  }

  return (
    <>
      {children}
      {/* Consent Modal for Policy Updates */}
      <ConsentModal
        isOpen={showConsentModal}
        onAccept={handleConsentResponse}
        onDecline={() => handleConsentResponse(false, false)}
        isUpdate={true}
        userEmail={currentUser?.email || undefined}
      />
    </>
  );
};

export default ProtectedRoute;